import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import "./billProfile.css";
import "./billVersionCompare.css";
import BillVersionText from '../components/BillVersionText';

const formatVersionDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `(${date.toLocaleDateString('en-US')})`;
};

const amendmentBadgeClass = (classification) => {
    if (classification === 'Gut-and-amend') return 'bvc-amendment-badge bvc-amendment-badge--gut-and-amend';
    if (classification === 'Substantive') return 'bvc-amendment-badge bvc-amendment-badge--substantive';
    if (classification === 'Non-substantive') return 'bvc-amendment-badge bvc-amendment-badge--non-substantive';
    return null;
};

const formatBillTitle = (bill) => {
    const sessionNum = bill.session_number ? parseInt(bill.session_number) : 0;
    return sessionNum > 0
        ? `${bill.bill_type}x${sessionNum} ${bill.bill_number}`
        : `${bill.bill_type} ${bill.bill_number}`;
};

function VersionColumn({ side, bill, sortedVersions, versionId, onSelect, versionCache, loadingId }) {
    const version = versionId ? versionCache[versionId] : null;
    const isLoading = versionId && loadingId === versionId;

    return (
        <div className={`col-md-6 bvc-column bvc-column-${side}`}>
            <div className="card mb-4 bvc-column-card">
                <div className="card-header bvc-column-header">
                    <select
                        className="form-select form-select-sm bvc-version-select"
                        value={versionId || ''}
                        onChange={(e) => onSelect(e.target.value)}
                    >
                        <option value="" disabled>Select a version…</option>
                        {sortedVersions.map(v => (
                            <option key={v.bill_version_id} value={v.bill_version_id}>
                                {v.version_status} {formatVersionDate(v.createdAt)}
                                {v.amendment_classification && v.amendment_classification !== 'Non-substantive' ? ` — ${v.amendment_classification}` : ''}
                            </option>
                        ))}
                    </select>
                    {version?.amendment_classification && (
                        <span className={amendmentBadgeClass(version.amendment_classification)}>{version.amendment_classification}</span>
                    )}
                    {version && (
                        <a
                            className="bvc-pdf-download bill-link"
                            href={`https://leginfo.legislature.ca.gov/faces/billPdf.xhtml?bill_id=${bill.bill_id}&version=${version.bill_version_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Download PDF of this version"
                        >
                            <i className="bi bi-file-earmark-pdf" />
                        </a>
                    )}
                </div>
                <div className="card-body bvc-column-body">
                    {isLoading ? (
                        <div className="d-flex justify-content-center mt-5">
                            <div className="spinner-border loading-spinner" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : version ? (
                        <div className="bill-content">
                            {version.amendment_summary && (
                                <div className="bvc-amendment-panel mb-3">
                                    <p className="bvc-amendment-text mb-1">{version.amendment_summary}</p>
                                    {version.amendment_reasoning && (
                                        <p className="bvc-amendment-reasoning mb-1">{version.amendment_reasoning}</p>
                                    )}
                                    {version.amendment_confidence && (
                                        <small className="text-muted">Confidence: {version.amendment_confidence}</small>
                                    )}
                                </div>
                            )}
                            <BillVersionText bill={bill} version={version} />
                        </div>
                    ) : (
                        <p className="text-muted text-center mt-5">Select a version to view its text.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function BillVersionCompare() {
    const { bill_id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [versionCache, setVersionCache] = useState({});
    const [loadingId, setLoadingId] = useState(null);

    const leftId = searchParams.get('left') || '';
    const rightId = searchParams.get('right') || '';

    useEffect(() => {
        if (!bill_id) return;
        setLoading(true);
        axios.get(`/api/legislation/bills/${bill_id}`)
            .then(res => {
                setBill(res.data);
                setError(null);
            })
            .catch(err => {
                console.error("Error fetching bill:", err);
                setError("Failed to load bill.");
            })
            .finally(() => setLoading(false));
    }, [bill_id]);

    const sortedVersions = bill?.BillVersions
        ? [...bill.BillVersions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        : [];

    // Default to the two most recent versions when no selection is present in the URL.
    useEffect(() => {
        if (!bill || sortedVersions.length === 0) return;
        if (leftId || rightId) return;

        const latest = sortedVersions[sortedVersions.length - 1];
        const previous = sortedVersions.length > 1 ? sortedVersions[sortedVersions.length - 2] : latest;

        const params = new URLSearchParams();
        params.set('left', latest.bill_version_id);
        params.set('right', previous.bill_version_id);
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bill]);

    const fetchVersion = useCallback((versionId) => {
        if (!versionId || versionCache[versionId]) return;

        // Reuse the already-loaded LatestVersion body instead of refetching it.
        if (bill?.LatestVersion?.bill_version_id === versionId) {
            setVersionCache(prev => ({ ...prev, [versionId]: bill.LatestVersion }));
            return;
        }

        setLoadingId(versionId);
        axios.get(`/api/legislation/version/${versionId}`)
            .then(res => {
                setVersionCache(prev => ({ ...prev, [versionId]: res.data }));
            })
            .catch(err => console.error("Error fetching bill version:", err))
            .finally(() => setLoadingId(null));
    }, [bill, versionCache]);

    useEffect(() => {
        if (leftId) fetchVersion(leftId);
        if (rightId) fetchVersion(rightId);
    }, [leftId, rightId, fetchVersion]);

    const updateSelection = (side, versionId) => {
        const params = new URLSearchParams(searchParams);
        params.set(side, versionId);
        setSearchParams(params);
    };

    const handleSwap = () => {
        const params = new URLSearchParams(searchParams);
        params.set('left', rightId);
        params.set('right', leftId);
        setSearchParams(params);
    };

    if (loading) return (
        <div className="d-flex position-absolute top-50 start-50 translate-middle">
            <div className="spinner-border loading-spinner" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );
    if (error) return <div className="container mt-5 text-center text-danger">{error}</div>;
    if (!bill) return <div className="container mt-5 text-center">Bill not found.</div>;

    return (
        <div className="container bvc-page">
            <div className="row mb-3 align-items-center">
                <div className="col-md-8">
                    <button
                        type="button"
                        className="btn btn-link bvc-back-link px-0"
                        onClick={() => navigate(`/legislation/bill/${bill.bill_id}`)}
                    >
                        <i className="bi bi-arrow-left me-1" />Back to Bill
                    </button>
                    <h4 className="bvc-title mb-0"><strong>{formatBillTitle(bill)}</strong> — Compare Versions</h4>
                    <h6 className="text-muted">{bill.title}</h6>
                </div>
                <div className="col-md-4 text-md-end bvc-compare-content">
                    <button
                        type="button"
                        className="btn btn-sm bvc-swap-btn"
                        disabled={!leftId || !rightId}
                        onClick={handleSwap}
                        title="Swap versions"
                    >
                        <i className="bi bi-arrow-left-right me-1" />Swap
                    </button>
                </div>
            </div>

            {/* Side-by-side comparison needs more room than a phone screen has — shown only at mobile widths */}
            <div className="bvc-mobile-notice">
                <i className="bi bi-arrows-angle-expand mb-2" />
                <p className="mb-2">Comparing bill versions side by side needs a bigger screen.</p>
                <p className="text-muted small mb-3">Please switch to a tablet or desktop to use this feature.</p>
                <button
                    type="button"
                    className="btn btn-sm bvc-swap-btn"
                    onClick={() => navigate(`/legislation/bill/${bill.bill_id}`)}
                >
                    Back to Bill
                </button>
            </div>

            <div className="bvc-compare-content">
                {sortedVersions.length < 2 && (
                    <div className="alert alert-info">
                        This bill only has one version available — there is nothing to compare yet.
                    </div>
                )}

                <div className="row">
                    <VersionColumn
                        side="left"
                        bill={bill}
                        sortedVersions={sortedVersions}
                        versionId={leftId}
                        onSelect={(id) => updateSelection('left', id)}
                        versionCache={versionCache}
                        loadingId={loadingId}
                    />
                    <VersionColumn
                        side="right"
                        bill={bill}
                        sortedVersions={sortedVersions}
                        versionId={rightId}
                        onSelect={(id) => updateSelection('right', id)}
                        versionCache={versionCache}
                        loadingId={loadingId}
                    />
                </div>
            </div>
        </div>
    );
}
